import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { restaurantConfig } from "../config/restaurantConfig";
import Reveal from "../components/Reveal";
import SectionEyebrow from "../components/SectionEyebrow";

export default function Menu() {
  const { menu, ui } = restaurantConfig;
  const t = ui.menu;
  const [active, setActive] = useState(menu[0].id);
  const current = menu.find((m) => m.id === active);

  return (
    <div data-testid="page-menu" className="pb-24">
      {/* Header */}
      <section className="relative pt-12 pb-16 md:pt-20 md:pb-24 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <SectionEyebrow className="justify-center inline-flex">{t.eyebrow}</SectionEyebrow>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl mt-6 italic" style={{ fontWeight: 400 }}>
              {t.title}
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-base sm:text-lg text-muted-foreground font-sans font-light">
              {t.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Tabs */}
      <section className="border-y border-border/40 sticky top-20 z-30 bg-background/85 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* এখানে w-full এবং justify-center যোগ করা হয়েছে ট্যাবগুলোকে মাঝখানে আনার জন্য */}
          <div className="flex w-full justify-center gap-2 sm:gap-6 overflow-x-auto hide-scrollbar py-2">
            {menu.map((cat) => {
              const isActive = cat.id === active;
              return (
                <button
                  key={cat.id}
                  data-testid={`menu-tab-${cat.id}`}
                  onClick={() => setActive(cat.id)}
                  className={`relative flex-shrink-0 px-4 sm:px-6 py-4 text-[11px] tracking-luxe uppercase transition-colors ${
                    isActive ? "text-burgundy dark:text-gold" : "text-foreground/60 hover:text-foreground"
                  }`}
                >
                  {cat.label}
                  {isActive && (
                    <motion.span
                      layoutId="menu-tab-underline"
                      className="absolute left-4 right-4 sm:left-6 sm:right-6 bottom-1.5 h-px bg-burgundy dark:bg-gold"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pt-16 md:pt-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`menu-content-${current.id}`}
            >
              <div className="text-center mb-16">
                <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl italic" style={{ fontWeight: 400 }}>
                  {current.label}
                </h2>
                <p className="mt-4 text-muted-foreground font-sans font-light">{current.blurb}</p>
                <div className="gold-divider w-24 mx-auto mt-6" />
              </div>

              {current.sections.map((section) => (
                <div key={section.title} className="mb-16">
                  <h3 className="font-serif text-2xl text-accent-line mb-8 tracking-wide italic text-center" style={{ fontWeight: 500 }}>
                    {section.title}
                  </h3>
                  <ul className="space-y-7">
                    {section.items.map((item, i) => (
                      <motion.li
                        key={item.name}
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                        data-testid={`menu-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="grid grid-cols-[1fr_auto] gap-x-6 items-baseline border-b border-dashed border-border/50 pb-6"
                      >
                        <div>
                          <h4 className="font-serif text-xl sm:text-2xl text-foreground italic" style={{ fontWeight: 500 }}>
                            {item.name}
                          </h4>
                          <p className="mt-2 text-sm text-muted-foreground font-sans font-light leading-relaxed max-w-xl">
                            {item.description}
                          </p>
                        </div>
                        <span className="font-serif text-xl sm:text-2xl text-accent-line whitespace-nowrap" style={{ fontWeight: 500 }}>
                          {item.price}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>

          <div className="text-center mt-16 text-xs tracking-luxe-sm uppercase text-muted-foreground">
            {t.footnote}
          </div>
        </div>
      </section>
    </div>
  );
}
